/**
 * Filter Utilities
 * Centralized filter logic for consistent behavior across components
 */

/**
 * Determines if all filters are active (no specific filters applied)
 * @param {Object} searchParams - The search parameters object
 * @param {Array} excludeKeys - Keys to exclude from the "all" check (e.g., 'search', 'ordering')
 * @returns {boolean} - True if no filters are applied, false otherwise
 */
export const isAllFiltersActive = (searchParams, excludeKeys = ['search', 'ordering']) => {
    if (!searchParams || typeof searchParams !== 'object') {
        return true;
    }

    return Object.entries(searchParams).every(([key, value]) =>
        excludeKeys.includes(key) || value === ''
    );
};

/**
 * Determines if a specific filter is active
 * @param {Object} searchParams - The search parameters object
 * @param {string} filterKey - The key to check
 * @param {*} expectedValue - The expected value for the filter to be active
 * @returns {boolean} - True if the specific filter is active
 */
export const isFilterActive = (searchParams, filterKey, expectedValue) => {
    if (!searchParams || typeof searchParams !== 'object') {
        return false;
    }

    return searchParams[filterKey] === expectedValue;
};

/**
 * Creates a quick filter configuration object
 * @param {string} key - The filter key
 * @param {string} label - The display label
 * @param {Object} searchParams - The search parameters object
 * @param {string} filterKey - The key to check in searchParams
 * @param {*} expectedValue - The expected value for the filter to be active
 * @returns {Object} - Quick filter configuration object
 */
export const createQuickFilter = (key, label, searchParams, filterKey, expectedValue) => ({
    key,
    label,
    active: isFilterActive(searchParams, filterKey, expectedValue)
});

/**
 * Creates an "All" filter configuration object
 * @param {string} label - The display label (e.g., "All Applications")
 * @param {Object} searchParams - The search parameters object
 * @param {Array} excludeKeys - Keys to exclude from the "all" check
 * @returns {Object} - "All" filter configuration object
 */
export const createAllFilter = (label, searchParams, excludeKeys = ['search', 'ordering']) => ({
    key: 'all',
    label,
    active: isAllFiltersActive(searchParams, excludeKeys)
});

/**
 * Creates a complete quick filters array for common patterns
 * @param {Object} searchParams - The search parameters object
 * @param {Object} options - Configuration options
 * @returns {Array} - Array of quick filter configuration objects
 */
export const createQuickFilters = (searchParams, options = {}) => {
    const {
        statusFilters = [],
        typeFilters = [],
        allLabel = 'All Items',
        excludeKeys = ['search', 'ordering']
    } = options;

    const filters = [];

    // Add status filters
    statusFilters.forEach(({ key, label, filterKey, expectedValue }) => {
        filters.push(createQuickFilter(key, label, searchParams, filterKey, expectedValue));
    });

    // Add type filters
    typeFilters.forEach(({ key, label, filterKey, expectedValue }) => {
        filters.push(createQuickFilter(key, label, searchParams, filterKey, expectedValue));
    });

    // Add "All" filter
    filters.push(createAllFilter(allLabel, searchParams, excludeKeys));

    return filters;
};

/**
 * Common filter configurations for different entity types
 */
export const filterConfigs = {
    applications: {
        statusFilters: [
            { key: 'pending', label: 'Pending', filterKey: 'status', expectedValue: 'pending' },
            { key: 'approved', label: 'Approved', filterKey: 'status', expectedValue: 'approved' },
            { key: 'rejected', label: 'Rejected', filterKey: 'status', expectedValue: 'rejected' }
        ],
        allLabel: 'All Applications'
    },

    members: {
        statusFilters: [
            { key: 'approved', label: 'Approved', filterKey: 'status', expectedValue: 'approved' },
            { key: 'pending', label: 'Pending', filterKey: 'status', expectedValue: 'pending' },
            { key: 'rejected', label: 'Rejected', filterKey: 'status', expectedValue: 'rejected' },
            { key: 'subscribed', label: 'Subscribed', filterKey: 'email_subscribed', expectedValue: 'true' }
        ],
        allLabel: 'All Members'
    },

    invoices: {
        statusFilters: [
            { key: 'draft', label: 'Draft', filterKey: 'status', expectedValue: 'draft' },
            { key: 'sent', label: 'Sent', filterKey: 'status', expectedValue: 'sent' },
            { key: 'paid', label: 'Paid', filterKey: 'status', expectedValue: 'paid' },
            { key: 'overdue', label: 'Overdue', filterKey: 'status', expectedValue: 'overdue' }
        ],
        typeFilters: [
            { key: 'renewal', label: 'Renewal', filterKey: 'invoice_type', expectedValue: 'renewal' },
            { key: 'new_member', label: 'New Member', filterKey: 'invoice_type', expectedValue: 'new_member' }
        ],
        allLabel: 'All Invoices'
    },

    distributors: {
        statusFilters: [
            { key: 'active', label: 'Active', filterKey: 'is_active', expectedValue: 'true' },
            { key: 'inactive', label: 'Inactive', filterKey: 'is_active', expectedValue: 'false' }
        ],
        allLabel: 'All Distributors'
    },

    users: {
        statusFilters: [
            { key: 'pending', label: 'Pending', filterKey: 'is_approved', expectedValue: 'false' },
            { key: 'approved', label: 'Approved', filterKey: 'is_approved', expectedValue: 'true' },
            { key: 'staff', label: 'Staff', filterKey: 'is_staff', expectedValue: 'true' },
            { key: 'superuser', label: 'Superusers', filterKey: 'is_superuser', expectedValue: 'true' },
            { key: 'active', label: 'Active', filterKey: 'is_active', expectedValue: 'true' },
            { key: 'verified', label: 'Verified', filterKey: 'is_email_verified', expectedValue: 'true' }
        ],
        allLabel: 'All Users'
    },

    financialPeriods: {
        statusFilters: [
            { key: 'active', label: 'Active', filterKey: 'is_active', expectedValue: 'true' },
            { key: 'upcoming', label: 'Upcoming', filterKey: 'status', expectedValue: 'upcoming' },
            { key: 'completed', label: 'Completed', filterKey: 'status', expectedValue: 'completed' }
        ],
        allLabel: 'All Periods'
    }
};

/**
 * Gets quick filters for a specific entity type
 * @param {string} entityType - The entity type (e.g., 'applications', 'members')
 * @param {Object} searchParams - The search parameters object
 * @param {Array} excludeKeys - Keys to exclude from the "all" check
 * @returns {Array} - Array of quick filter configuration objects
 */
export const getQuickFilters = (entityType, searchParams, excludeKeys = ['search', 'ordering']) => {
    const config = filterConfigs[entityType];
    if (!config) {
        console.warn(`No filter configuration found for entity type: ${entityType}`);
        return [createAllFilter('All Items', searchParams, excludeKeys)];
    }

    return createQuickFilters(searchParams, {
        ...config,
        excludeKeys
    });
};

export default {
    isAllFiltersActive,
    isFilterActive,
    createQuickFilter,
    createAllFilter,
    createQuickFilters,
    getQuickFilters,
    filterConfigs
};
